import React, { useState, useEffect, useCallback, useMemo } from "react";
import TeacherHeader from "../common/TeacherHeader";
import Footer from "../common/Footer";
import { toast, ToastContainer } from "react-toastify";
import axios from "axios";
import { baseUrl } from "../../Utility/constants";
import { getSchoolId, fetchSubjectGroupMaster } from "../../Utility/commonFunctions";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const SummaryReport = () => {
  const [schoolId, setSchoolId] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const [standards, setStandards] = useState([]);
  const [sections, setSections] = useState([]);
  const [streams, setStreams] = useState([]);
  const [subjectGroups, setSubjectGroups] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [students, setStudents] = useState([]);

  const [selected, setSelected] = useState({
    standardId: "",
    sectionId: "",
    streamId: "",
    subjectGroupId: "",
    subjectId: "",
  });

  // ✅ Load initial data
  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const [id, groupData, streamRes] = await Promise.all([
          getSchoolId(),
          fetchSubjectGroupMaster(),
          axios.get(`${baseUrl}/api/StreamMaster`),
        ]);
        setSchoolId(id);
        setSubjectGroups(groupData);
        setStreams(streamRes.data);
      } catch (err) {
        console.error("Initial data load failed", err);
      }
    };
    loadInitialData();
  }, []);

  // ✅ Fetch Standards
  useEffect(() => {
    if (!schoolId) return;
    axios
      .get(`${baseUrl}/api/StandardMaster/GetStandardBySchool?id=${schoolId}`)
      .then((res) => setStandards(res.data))
      .catch(() => setStandards([]));
  }, [schoolId, refreshKey]);

  // ✅ Fetch Sections
  useEffect(() => {
    if (!selected.standardId) {
      setSections([]);
      return;
    }
    axios
      .get(`${baseUrl}/api/SectionMaster/GetSectionByStandardID?id=${selected.standardId}`)
      .then((res) => setSections(res.data))
      .catch(() => setSections([]));
  }, [selected.standardId]);

  // ✅ Fetch Subjects
  const fetchSubjects = useCallback(async () => {
    const { standardId, streamId, subjectGroupId } = selected;
    if (!standardId || !streamId || !subjectGroupId) {
      setSubjects([]);
      return;
    }
    try {
      const res = await axios.get(`${baseUrl}/api/SubjectMaster/GetSubjectMasterByClassAndStream`, {
        params: {
          classId: standardId,
          streamId,
          subjectGroup: subjectGroupId,
          teacherId: localStorage.getItem("userID"),
          userType: localStorage.getItem("Role"),
        },
      });
      setSubjects(res.data);
    } catch (err) {
      if (err.response?.status === 404) setSubjects([]);
      else console.error("Error fetching subjects:", err);
    }
  }, [selected.standardId, selected.streamId, selected.subjectGroupId]);

  useEffect(() => {
    fetchSubjects();
  }, [fetchSubjects]);

  const fetchStudents = useCallback(async () => {
    setStudents([]);

    const { standardId, sectionId, streamId, subjectGroupId, subjectId } = selected;

    if (!standardId || !sectionId || !streamId || !subjectGroupId || !subjectId) return;

    try {
      const { data } = await axios.get(
        `${baseUrl}/api/MarksFeeding/StudentExamSummary`,
        {
          params: {
            StandardId: standardId,
            SectionId: sectionId,
            streamId,
            subjectGroup: subjectGroupId,
            subjectId
          }
        }
      );

      setStudents(data || []);
    } catch (err) {
      console.error("Error fetching summary:", err);
      setStudents([]);
    }
  }, [selected]);

  useEffect(() => { fetchStudents(); }, [fetchStudents]);

  // ✅ Handle Input Change
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSelected((prev) => ({ ...prev, [name]: value }));
    if (name === "subjectId") {
      setStudents([]);
    }
  };


  const dropdowns = useMemo(
    () => [
      { label: "Standard", name: "standardId", options: standards, labelKey: "standardName", valueKey: "id" },
      { label: "Section", name: "sectionId", options: sections, labelKey: "name", valueKey: "id" },
      { label: "Stream", name: "streamId", options: streams, labelKey: "name", valueKey: "id" },
      { label: "Group", name: "subjectGroupId", options: subjectGroups, labelKey: "subjectGroupName", valueKey: "subjectGroupId" },
      { label: "Subject", name: "subjectId", options: subjects, labelKey: "subjectName", valueKey: "subjectId" },
    ],
    [standards, sections, streams, subjectGroups, subjects]
  );

  const exportToExcel = () => {
    if (!students.length) {
      toast.warning("No data to export");
      return;
    }

    const exportData = students.map((stu, index) => ({
      "Sr No": index + 1,
      "Seat No": stu.examSeatNo,
      "Name": stu.name,

      "Unit 1": stu.unit1 ?? 0,
      "Unit 2": stu.unit2 ?? 0,
      "Term 1": stu.term1 ?? 0,

      "Total A": stu.totalA ?? 0,

      "Theory": stu.finalTheory ?? 0,
      "Oral/Practical": stu.finalOral ?? 0,

      "Total B": stu.totalB ?? 0,

      "Total A + B": stu.grandTotal ?? 0,
      "Average": stu.average ?? 0
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();

    XLSX.utils.book_append_sheet(workbook, worksheet, "Summary Report");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array"
    });

    const fileData = new Blob([excelBuffer], {
      type: "application/octet-stream"
    });

    const fileName = `Summary_Report_${Date.now()}.xlsx`;
    saveAs(fileData, fileName);
  };

  return (
    <>
      <TeacherHeader />
      <div className="container mt-5 mb-5">
        <div className="row g-3">
          {dropdowns.map(({ label, name, options, labelKey, valueKey }) => (
            <div className="col-md-2" key={name}>
              <label className="form-label">{label}</label>
              <select className="form-select" name={name} value={selected[name]} onChange={handleInputChange}>
                <option value="">Select {label}</option>
                {options.map((opt) => <option key={opt[valueKey]} value={opt[valueKey]}>{opt[labelKey]}</option>)}
              </select>
            </div>
          ))}
        </div>
        <div className="d-flex justify-content-end mt-3">
          <button className="btn btn-success btn-sm" onClick={exportToExcel}>
            Export to Excel
          </button>
        </div>
        <table className="table table-striped table-hover mt-3">
          <thead>
            <tr>
              <th>#</th>
              <th>Exam Seat No</th>
              <th>Name</th>
              <th>Unit 1</th>
              <th>Unit 2</th>
              <th>Term 1</th>
              <th>Total A</th>
              <th>Theory</th>
              <th>Oral / Practical</th>
              <th>Total B</th>
              <th>Total A + B</th>
              <th>Average</th>
            </tr>
          </thead>
          <tbody>
            {students.map((stu, index) => (
              <tr key={stu.studentId}>
                <td>{index + 1}</td>
                <td>{stu.examSeatNo}</td>
                <td>{stu.name}</td>

                <td>{stu.unit1 === -1 ? "A" : (stu.unit1 ?? 0)}</td>
                <td>{stu.unit2 === -1 ? "A" : (stu.unit2 ?? 0)}</td>
                <td>{stu.term1 === -1 ? "A" : (stu.term1 ?? 0)}</td>

                <td><b>{stu.totalA ?? 0}</b></td>

                <td>{stu.finalTheory === -1 ? "A" : (stu.finalTheory ?? 0)}</td>
                <td>{stu.finalOral === -1 ? "A" : (stu.finalOral ?? 0)}</td>

                <td><b>{stu.totalB ?? 0}</b></td>

                <td><b>{stu.grandTotal ?? 0}</b></td>
                <td>{stu.average ?? 0}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <Footer />
      <ToastContainer />
    </>
  );
};

export default SummaryReport;
